import { useState, useEffect, useRef } from 'react';
import { Search, Plus, Wrench, TrendingUp, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTranslations } from '../i18n';
import type { SparePart, SparePartSearchResult } from '../types/spareParts';
import { AlertModal } from '../components/ui/AlertModal';

interface SparePartSelectorProps {
    carId: string;
    existingPartIds?: string[];
    onSelect: (part: SparePart) => void;
    onCreateNew: () => void;
    onClose: () => void;
}

export function SparePartSelector({ carId, existingPartIds = [], onSelect, onCreateNew, onClose }: SparePartSelectorProps) {
    const { t } = useTranslations();
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState<SparePartSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [alertConfig, setAlertConfig] = useState({ isOpen: false, title: '', message: '' });
    const searchInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // iOS Keyboard Fix: Use Visual Viewport API
    useEffect(() => {
        if (!window.visualViewport) return;

        const vvp = window.visualViewport;
        const handleViewportChange = () => {
            if (containerRef.current) {
                containerRef.current.style.height = `${vvp.height}px`;
                containerRef.current.style.top = `${vvp.offsetTop}px`;
                if (window.scrollY !== 0) {
                    window.scrollTo(0, 0);
                }
            }
        };

        vvp.addEventListener('resize', handleViewportChange);
        vvp.addEventListener('scroll', handleViewportChange);
        handleViewportChange();

        // Lock background thoroughly to prevent iOS bounce leakage
        const originalOverflow = document.documentElement.style.overflow;
        const originalBodyOverflow = document.body.style.overflow;
        const originalBodyPosition = document.body.style.position;
        const originalBodyWidth = document.body.style.width;
        const originalBodyHeight = document.body.style.height;

        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100dvh';

        return () => {
            vvp.removeEventListener('resize', handleViewportChange);
            vvp.removeEventListener('scroll', handleViewportChange);
            document.documentElement.style.overflow = originalOverflow;
            document.body.style.overflow = originalBodyOverflow;
            document.body.style.position = originalBodyPosition;
            document.body.style.width = originalBodyWidth;
            document.body.style.height = originalBodyHeight;
        };
    }, []);

    // Auto-focus search input on mount
    useEffect(() => {
        searchInputRef.current?.focus();
    }, []);

    // Load all parts on mount, then filter client-side for instant results
    useEffect(() => {
        loadParts();
    }, [carId]);

    const loadParts = async () => {
        setLoading(true);
        try {
            // Fetch all parts for this car with statistics
            const { data: parts, error } = await supabase
                .from('spare_parts')
                .select(`
          *,
          spare_part_images (
            id,
            image_url,
            image_url_thumb,
            log_entry_id,
            note_id
          )
        `)
                .eq('car_id', carId)
                .order('last_used_at', { ascending: false, nullsFirst: false });

            if (error) throw error;

            // Enrich with additional computed data
            const enrichedParts: SparePartSearchResult[] = await Promise.all(
                (parts || []).map(async (part: any) => {
                    // Get last price from most recent usage
                    const { data: lastUsage } = await supabase
                        .from('log_entry_parts')
                        .select('price_per_unit, purchased_at')
                        .eq('spare_part_id', part.id)
                        .not('price_per_unit', 'is', null)
                        .order('purchased_at', { ascending: false })
                        .limit(1)
                        .maybeSingle();

                    return {
                        ...part,
                        images: part.spare_part_images || [],
                        last_price: lastUsage?.price_per_unit,
                        image_count: part.spare_part_images?.length || 0,
                        usage_count: part.total_uses || 0
                    };
                })
            );

            setResults(enrichedParts);
        } catch (error) {
            console.error('Error loading spare parts:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter results based on search query
    const filteredResults = results.filter(part => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            part.name.toLowerCase().includes(query) ||
            part.manufacturer?.toLowerCase().includes(query) ||
            part.part_code?.toLowerCase().includes(query)
        );
    });

    const handleSelect = (part: SparePart) => {
        if (existingPartIds.includes(part.id)) {
            setAlertConfig({
                isOpen: true,
                title: t.common?.error || 'Error',
                message: t.spareParts?.alreadyAdded || 'This part is already added'
            });
            return;
        }
        onSelect(part);
    };

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 flex flex-col overflow-hidden text-gray-900 dark:text-gray-100 z-[100] bg-gradient-theme"
        >
            {/* Custom Search Navigation Bar */}
            <div className="flex-shrink-0 z-30 non-draggable">
                <div className="max-w-2xl mx-auto flex items-center px-4 h-[68px] gap-3">
                    <div className="relative flex-1 group">
                        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t.spareParts?.searchPlaceholder || 'Search by name, manufacturer or code...'}
                            className="w-full pl-12 pr-4 py-2.5 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-400/50 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 shadow-sm text-[15px]"
                        />
                    </div>
                    <button
                        onClick={onClose}
                        className="text-blue-600 dark:text-blue-400 font-bold text-[15px] px-2 py-2 active:opacity-60 transition-opacity whitespace-nowrap"
                    >
                        {t.common?.cancel || 'Cancel'}
                    </button>
                </div>
            </div>

            <div className="flex-1 flex flex-col w-full overflow-hidden">
                {/* Results List (Scrollable Area) */}
                <div className="flex-1 overflow-y-auto pb-6 overscroll-none">
                    <div className="max-w-2xl mx-auto px-4 mt-2">
                        {/* Always visible "Create New" button as the first item */}
                        <div className="mb-4">
                            <button
                                onClick={onCreateNew}
                                className="w-full p-3 bg-blue-600/10 dark:bg-blue-400/10 border border-blue-500/20 dark:border-blue-400/20 rounded-xl flex items-center gap-3 transition-all hover:bg-blue-600/20 dark:hover:bg-blue-400/20 active:scale-[0.99] group"
                            >
                                <div className="w-10 h-10 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform">
                                    <Plus size={20} strokeWidth={3} className="text-white" />
                                </div>
                                <div className="text-left">
                                    <div className="font-bold text-blue-700 dark:text-blue-300 text-sm uppercase tracking-wider">
                                        {t.spareParts?.createNew || 'Create New Part'}
                                    </div>
                                </div>
                            </button>
                        </div>

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-24">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4" />
                                <p className="text-gray-500 text-sm font-medium italic">{t.common?.loading || 'Loading'}...</p>
                            </div>
                        ) : (searchQuery.length === 0 && results.length === 0) ? (
                            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/5 flex items-center justify-center mb-4">
                                    <Wrench size={32} className="text-gray-400 dark:text-gray-600" />
                                </div>
                                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium leading-relaxed max-w-[240px]">
                                    {t.spareParts.selectorDescription}
                                </p>
                            </div>
                        ) : filteredResults.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 px-6 bg-white/50 dark:bg-slate-900/20 border border-dashed border-gray-200 dark:border-white/10 rounded-2xl">
                                <Wrench size={24} className="text-gray-300 dark:text-gray-600 mb-2" />
                                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                                    {t.spareParts?.noResults || 'No parts found'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="divide-y divide-gray-100 dark:divide-white/5 bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/5 rounded-2xl shadow-sm overflow-hidden">
                                    {filteredResults.map((part) => {
                                        const isAlreadyAdded = existingPartIds.includes(part.id);
                                        return (
                                            <button
                                                key={part.id}
                                                onClick={() => handleSelect(part)}
                                                className={`w-full group text-left flex items-start gap-4 p-4 transition-all active:scale-[0.99] ${isAlreadyAdded
                                                        ? 'bg-blue-50/40 dark:bg-blue-400/5 hover:bg-blue-50/60 dark:hover:bg-blue-400/10'
                                                        : 'hover:bg-gray-50/80 dark:hover:bg-white/5'
                                                    }`}
                                            >
                                                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${isAlreadyAdded
                                                        ? 'bg-blue-600/10 dark:bg-blue-400/10'
                                                        : 'bg-green-50 dark:bg-green-900/20'
                                                    }`}>
                                                    <Wrench size={22} className={isAlreadyAdded ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'} />
                                                </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2 mb-1">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <h4 className="font-bold text-gray-900 dark:text-white text-[15px] truncate">
                                                            {part.name}
                                                        </h4>
                                                        {part.manufacturer && (
                                                            <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded tracking-wide flex-shrink-0">
                                                                {part.manufacturer}
                                                            </span>
                                                        )}
                                                        {part.part_code && (
                                                            <span className="text-[10px] font-black text-gray-400 bg-gray-100 dark:bg-white/5 px-1.5 py-0.5 rounded uppercase tracking-widest flex-shrink-0">
                                                                {part.part_code}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        {(part.last_price || 0) > 0 && (
                                                            <p className="font-bold text-gray-900 dark:text-white text-[15px]">
                                                                {part.last_price?.toFixed(2)} €
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3 text-[13px] text-gray-500 font-medium">
                                                    {(part.usage_count ?? 0) > 0 && (
                                                        <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400 font-bold">
                                                            <TrendingUp size={12} />
                                                            {t.spareParts?.statistics?.usedTimes?.replace('{count}', String(part.usage_count ?? 0)) || `${part.usage_count}x used`}
                                                        </span>
                                                    )}
                                                    {part.images && part.images.length > 0 && (
                                                        <span className="flex items-center gap-1 text-blue-500 dark:text-blue-400 ml-auto">
                                                            <ImageIcon size={14} />
                                                            {part.images.length}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    )})}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <AlertModal
                    isOpen={alertConfig.isOpen}
                    onClose={() => setAlertConfig({ ...alertConfig, isOpen: false })}
                    title={alertConfig.title}
                    message={alertConfig.message}
                />
            </div>
        </div>
    );
}
